import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import * as R from 'ramda';

import { AuthContext } from 'authentication/AuthContext';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import DataDisplayItem from '../../components/DataDisplayItem';
import DistributionWizardLayout from './DistributionWizardLayout';
import ParentShipmentSummary from './ParentShipmentSummary';
import { useCreateDistributionsMutation } from '../../batchesEndpoints';

const infoContainerStyle = {
  display: 'grid',
  gridTemplateColumns: { lg: '1fr 1fr' },
  rowGap: { lg: 3, xs: 2 },
};

const DistributionSummary = ({
  distributionsState,
  onClose,
  onComplete,
  onPrev,
  onReset,
  shipmentData,
  totalProposedVolume,
}) => {
  const navigate = useNavigate();
  const { distributions } = distributionsState;
  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const dataCompanies = affiliates.reduce((a, c) => {
    return [c.dataCompanyId, ...a];
  }, []);
  const dataCompanyId = R.path(['shipper', 'dataCompanyId'], shipmentData);

  if (!dataCompanies.includes(dataCompanyId)) {
    navigate(`/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}`);
  }

  const [submit, { data, isSuccess, isFetching, isError, error }] =
    useCreateDistributionsMutation();

  // inventoryOwnerToAffiliate :: InventoryOwner -> Affiliate
  const inventoryOwnerToAffiliate = ({ inventoryOwnerIdName: companyCustomerIdName }) => ({
    companyCustomerIdName,
    dataCompanyId: parseInt(dataCompanyId),
  });

  // recipientToAffiliate :: Recipient -> Affiliate
  const recipientToAffiliate = ({ recipientIdName: companyCustomerIdName }) => ({
    companyCustomerIdName,
    dataCompanyId: parseInt(dataCompanyId),
  });

  const distributionTransformations = {
    chainRecipients: R.map(recipientToAffiliate),
    inventoryOwner: inventoryOwnerToAffiliate,
    locationId: R.prop('id'),
  };

  const handleSubmit = () =>
    submit({
      body: R.evolve(
        {
          distributions: R.map(R.evolve(distributionTransformations)),
          shipmentId: R.compose(parseInt),
        },
        distributionsState
      ),
    });

  const buttons = (
    <>
      {isSuccess ? (
        <Button onClick={onReset} startIcon={<AddIcon />} variant="outlined">
          New Distribution
        </Button>
      ) : (
        <Button onClick={onPrev} sx={{ width: 104 }} variant="outlined">
          Back
        </Button>
      )}

      {isSuccess || isError ? (
        <Button onClick={onComplete} sx={{ width: 104 }}>
          Close
        </Button>
      ) : (
        <Button disabled={isFetching} onClick={handleSubmit} sx={{ width: 104 }}>
          Submit
        </Button>
      )}
    </>
  );

  return (
    <DistributionWizardLayout
      buttons={buttons}
      onClose={R.or(isSuccess, isError) ? onComplete : onClose}
      pageTitle="Add New Distribution"
      parentDetails={
        <ParentShipmentSummary
          shipmentData={shipmentData}
          totalProposedVolume={totalProposedVolume}
        />
      }
    >
      {isSuccess && (
        <Stack sx={{ alignItems: 'center', pb: 7, pt: 5 }}>
          <CheckRoundedIcon sx={{ color: 'primary.main', fontSize: 70 }} />
          <Typography sx={{ color: 'text.primary', mb: 2 }} variant="h5">
            Your Distribution(s) were Successfully Submitted!
          </Typography>
          <Typography sx={{ textAlign: 'center' }} variant="body1">
            {data.message}
          </Typography>
        </Stack>
      )}

      {isError && (
        <Stack sx={{ alignItems: 'center', pb: 7, pt: 5 }}>
          <CloseRoundedIcon sx={{ color: 'error.main', fontSize: 70 }} />
          <Typography sx={{ color: 'text.primary', mb: 2 }} variant="h5">
            Your Distribution(s) Could Not be Created.
          </Typography>
          <Typography sx={{ textAlign: 'center' }} variant="body1">
            {error.data.title}
          </Typography>
        </Stack>
      )}

      <Card
        elevation={1}
        sx={{
          pb: 11,
          pt: { md: 3, xs: 2 },
          px: { md: 3, xs: 2 },
        }}
      >
        <CardHeader sx={{ padding: 0 }} title="Summary" />
        {distributions.map(
          (
            {
              inventoryOwner,
              locationId,
              chainRecipients,
              reconsignmentControl,
              shipmentChain,
              volume,
            },
            index
          ) => {
            const count = R.add(index, 1);

            const renderShipmentChain = recipients =>
              recipients.map(({ recipientIdName, recipientName }) => (
                <Box key={recipientIdName}>{recipientName}</Box>
              ));

            return (
              <CardContent
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                sx={{
                  '&:last-child': {
                    paddingBottom: 0,
                  },
                  padding: 0,
                }}
              >
                <Stack spacing={2} sx={{ py: { md: 3, xs: 2 } }}>
                  <Typography variant="subtitle1">Distribution {count}</Typography>
                  <Box sx={infoContainerStyle}>
                    <DataDisplayItem
                      label="Inventory Owner"
                      value={inventoryOwner.inventoryOwnerName}
                    />
                    <DataDisplayItem
                      label="Reconsignment Control"
                      value={reconsignmentControl ? 'Any Controlled' : 'Any Uncontrolled'}
                    />
                    <DataDisplayItem label="Location" value={locationId.locationName} />
                    <DataDisplayItem
                      additionalStackStyling={{
                        gridColumn: '2/3',
                        gridRow: '2/4',
                      }}
                      label="Shipment Chain"
                      value={shipmentChain ? renderShipmentChain(chainRecipients) : '--'}
                    />
                    <DataDisplayItem
                      label="Volume"
                      value={`${formatNumberWithoutDecimals(volume)} bbl`}
                    />
                  </Box>
                </Stack>
                {count < distributions.length && <Divider />}
              </CardContent>
            );
          }
        )}
      </Card>
    </DistributionWizardLayout>
  );
};

DistributionSummary.propTypes = {
  distributionsState: PropTypes.shape({
    distributions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  shipmentData: PropTypes.shape({}).isRequired,
  totalProposedVolume: PropTypes.number.isRequired,
};

export default DistributionSummary;
